// TelcoTrack — Nedodělky (construction defects / punch list)

/** MinIO path prefix. */
export const NEDODELKY_STORAGE_PREFIX = 'nedodelky' as const;

export const NEDODELKY_THUMBNAIL_MAX_EDGE_PX = 256;

export const NEDODELKY_PRIORITIES = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

export type NedodelkyPriority =
  (typeof NEDODELKY_PRIORITIES)[keyof typeof NEDODELKY_PRIORITIES];

export const NEDODELKY_PRIORITY_VALUES: readonly NedodelkyPriority[] = [
  NEDODELKY_PRIORITIES.LOW,
  NEDODELKY_PRIORITIES.NORMAL,
  NEDODELKY_PRIORITIES.HIGH,
  NEDODELKY_PRIORITIES.CRITICAL,
];

export const NEDODELKY_PRIORITY_LABELS: Record<NedodelkyPriority, string> = {
  low: 'Nízká',
  normal: 'Běžná',
  high: 'Vysoká',
  critical: 'Kritická',
};

export const NEDODELKY_STATUSES = {
  OPEN: 'open',
  DONE: 'done',
  CANCELLED: 'cancelled',
} as const;

export type NedodelkyStatus =
  (typeof NEDODELKY_STATUSES)[keyof typeof NEDODELKY_STATUSES];

export const NEDODELKY_STATUS_VALUES: readonly NedodelkyStatus[] = [
  NEDODELKY_STATUSES.OPEN,
  NEDODELKY_STATUSES.DONE,
  NEDODELKY_STATUSES.CANCELLED,
];

export const NEDODELKY_STATUS_LABELS: Record<NedodelkyStatus, string> = {
  open: 'Otevřené',
  done: 'Hotovo',
  cancelled: 'Zrušeno',
};

export const NEDODELKY_SOURCES = {
  CAMERA: 'camera',
  GALLERY: 'gallery',
} as const;

export type NedodelkySource =
  (typeof NEDODELKY_SOURCES)[keyof typeof NEDODELKY_SOURCES];

export const NEDODELKY_SOURCE_VALUES: readonly NedodelkySource[] = [
  NEDODELKY_SOURCES.CAMERA,
  NEDODELKY_SOURCES.GALLERY,
];

export const NEDODELKY_LAYER_ID = 'nedodelky' as const;
export const NEDODELKY_LAYER_LABEL = 'Nedodělky';
export const MAP_FILTER_NEDODELKY_ID = `overlay:${NEDODELKY_LAYER_ID}` as const;

export const NEDODELKY_UI = {
  MENU_LABEL: 'Nedodělky',
  LAYER_LABEL: NEDODELKY_LAYER_LABEL,
  NO_GPS: 'Chybí poloha GPS — nelze uložit nedodělek.',
  NOTE_REQUIRED: 'Vyplňte poznámku.',
  POLYGON_REQUIRED: 'Vyberte stavbu (polygon).',
  UNASSIGNED_LABEL: 'Nepřiřazeno',
} as const;

/** Exclusive primary list scopes (Android chips / web segmented). */
export const NEDODELKY_LIST_SCOPES = {
  MINE: 'mine',
  TO_SOLVE: 'to_solve',
  DONE: 'done',
} as const;

export type NedodelkyListScope =
  (typeof NEDODELKY_LIST_SCOPES)[keyof typeof NEDODELKY_LIST_SCOPES];

export const NEDODELKY_LIST_SCOPE_DEFAULT: NedodelkyListScope = NEDODELKY_LIST_SCOPES.MINE;

export const NEDODELKY_LIST_SCOPE_TABS: readonly {
  id: NedodelkyListScope;
  label: string;
}[] = [
  { id: NEDODELKY_LIST_SCOPES.MINE, label: 'Moje' },
  { id: NEDODELKY_LIST_SCOPES.TO_SOLVE, label: 'K řešení' },
  { id: NEDODELKY_LIST_SCOPES.DONE, label: 'Hotové' },
] as const;

/** API `status` for a primary scope. Moje and K řešení → open; Hotové → done. */
export function nedodelkyListScopeStatus(
  scope: NedodelkyListScope,
): typeof NEDODELKY_STATUSES.OPEN | typeof NEDODELKY_STATUSES.DONE {
  return scope === NEDODELKY_LIST_SCOPES.DONE
    ? NEDODELKY_STATUSES.DONE
    : NEDODELKY_STATUSES.OPEN;
}

export function isNedodelkyListScope(value: unknown): value is NedodelkyListScope {
  return (
    typeof value === 'string' &&
    (Object.values(NEDODELKY_LIST_SCOPES) as string[]).includes(value)
  );
}

export function isNedodelkyPriority(value: unknown): value is NedodelkyPriority {
  return (
    typeof value === 'string' &&
    (NEDODELKY_PRIORITY_VALUES as readonly string[]).includes(value)
  );
}

export function isNedodelkyStatus(value: unknown): value is NedodelkyStatus {
  return (
    typeof value === 'string' &&
    (NEDODELKY_STATUS_VALUES as readonly string[]).includes(value)
  );
}

export function isNedodelkySource(value: unknown): value is NedodelkySource {
  return (
    typeof value === 'string' &&
    (NEDODELKY_SOURCE_VALUES as readonly string[]).includes(value)
  );
}

export function isValidNedodelkyLngLat(lng: unknown, lat: unknown): boolean {
  const lon = typeof lng === 'number' ? lng : Number(lng);
  const la = typeof lat === 'number' ? lat : Number(lat);
  return (
    Number.isFinite(lon) &&
    Number.isFinite(la) &&
    lon >= -180 &&
    lon <= 180 &&
    la >= -90 &&
    la <= 90
  );
}

export function getNedodelkyStorageKey(fileName: string): string {
  return `${NEDODELKY_STORAGE_PREFIX}/${fileName}`;
}

export function getNedodelkyThumbnailKey(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '') || fileName;
  return `${NEDODELKY_STORAGE_PREFIX}/thumbs/${base}.jpg`;
}
